import React, { useState, useMemo } from 'react'
import { Container } from '../../../components'
import { useParams } from 'react-router-dom'
import {
    Theme,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    IconButton,
    Drawer,
    Typography,
    TableContainer,
    Paper,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Checkbox,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    CircularProgress,
    TableSortLabel,
    TextField,
    InputAdornment,
    Chip,
} from '@material-ui/core'
import MoreVertIcon from '@material-ui/icons/MoreVert'
import CloseIcon from '@material-ui/icons/Close'
import UndoIcon from '@material-ui/icons/Undo'
import SearchIcon from '@material-ui/icons/Search'
import { FixedSizeList } from 'react-window'
import { useMergeHistory } from '../../../hooks/documentAudit/useMergeHistory'
import { format, subDays, subHours } from 'date-fns'
import {
    MergeOperation,
    MergeTransaction,
    ResidentTermDirectoryRow,
} from '../../../models/DocumentAudit/types'
import { BaseUser } from '../../../models/Users'
import {
    PERMISSION_OPTIONS,
    defaultPermissible,
} from '../../../models/Permissions/types'

type SortField = 'user' | 'date' | 'operations'
type SortOrder = 'asc' | 'desc'

interface SortConfig {
    field: SortField
    order: SortOrder
}

// Mock data generator for development testing
const generateMockData = (count: number): MergeTransaction[] => {
    const createMockUser = (
        id: number,
        name: string,
        email: string,
    ): BaseUser => ({
        id,
        name,
        email,
        ...defaultPermissible,
    })

    const users = [
        createMockUser(1, 'John Smith', 'john@example.com'),
        createMockUser(2, 'Alice Johnson', 'alice@example.com'),
        createMockUser(3, 'Bob Wilson', 'bob@example.com'),
    ]

    const customerNames = [
        'Sarah Parker',
        'Michael Brown',
        'Emma Davis',
        'James Wilson',
        'Olivia Taylor',
        'William Anderson',
        'Sophia Martinez',
        'Lucas Thompson',
    ]

    const units = [
        '101A',
        '102B',
        '103C',
        '201A',
        '202B',
        '203C',
        '301A',
        '302B',
    ]

    return Array.from({ length: count }, (_, i) => {
        const operationsCount = Math.floor(Math.random() * 4) + 1 // 1 to 4 operations
        const created_date = subHours(
            subDays(new Date(), Math.floor(i / 3)),
            i % 24,
        ).toISOString()
        const created_by = users[i % users.length]

        return {
            id: i + 1,
            lease_audit: 1,
            status: 'COMPLETED',
            notes: null,
            created_date,
            created_by,
            modified_date: created_date,
            operations: Array.from({ length: operationsCount }, (_, j) => {
                const createDirectory = (
                    customerId: number,
                ): ResidentTermDirectoryRow => ({
                    id: i * 100 + customerId,
                    customer: {
                        id: customerId,
                        name:
                            customerNames[
                                Math.floor(Math.random() * customerNames.length)
                            ],
                        created_date: created_date,
                        email: null,
                        phone: null,
                    },
                    lease_start: created_date,
                    lease_end: created_date,
                    bldg_unit: units[Math.floor(Math.random() * units.length)],
                    values: [],
                    status: 'MATCHED',
                })

                return {
                    id: i * 10 + j + 1,
                    excel_directory: createDirectory(i * 100 + 1),
                    pdf_directory: createDirectory(i * 100 + 2),
                    merged_directory: createDirectory(i * 100 + 3),
                    status: 'COMPLETED',
                    error_message: null,
                    deliverable_values: [],
                    created_date: created_date,
                    modified_date: created_date,
                }
            }),
        }
    })
}

// Development flag - set to true to use mock data
const USE_MOCK_DATA = false
const MOCK_ROW_COUNT = 50

interface Props {
    theme: Theme
    isActive: boolean
}

const getStatusColor = (status: MergeTransaction['status'], theme: Theme) => {
    switch (status) {
        case 'COMPLETED':
            return theme.palette.success.main
        case 'FAILED':
            return theme.palette.error.main
        case 'REVERTED':
            return theme.palette.grey[500]
        case 'PARTIALLY_REVERTED':
            return theme.palette.warning.main
        default:
            return theme.palette.grey[500]
    }
}

const getStatusLabel = (status: MergeTransaction['status']) => {
    switch (status) {
        case 'COMPLETED':
            return 'Completed'
        case 'FAILED':
            return 'Failed'
        case 'REVERTED':
            return 'Reverted'
        case 'PARTIALLY_REVERTED':
            return 'Partially Reverted'
        default:
            return status
    }
}

const getOperationStatusColor = (
    status: MergeOperation['status'],
    theme: Theme,
) => {
    switch (status) {
        case 'COMPLETED':
            return theme.palette.success.main
        case 'FAILED':
            return theme.palette.error.main
        case 'REVERTED':
            return theme.palette.grey[500]
        default:
            return theme.palette.grey[500]
    }
}

const getOperationStatusLabel = (status: MergeOperation['status']) => {
    switch (status) {
        case 'COMPLETED':
            return 'Completed'
        case 'FAILED':
            return 'Failed'
        case 'REVERTED':
            return 'Reverted'
        default:
            return status
    }
}

export const HistoryPage = ({ theme, isActive }: Props) => {
    const { id } = useParams<{ id?: string }>()
    const {
        mergeHistory: actualMergeHistory,
        isLoading,
        revertMergeTransaction,
    } = useMergeHistory(Number(id))

    // Use mock data in development
    const mergeHistory = USE_MOCK_DATA
        ? generateMockData(MOCK_ROW_COUNT)
        : actualMergeHistory

    const [
        selectedTransaction,
        setSelectedTransaction,
    ] = useState<MergeTransaction | null>(null)
    const [isRevertDialogOpen, setIsRevertDialogOpen] = useState(false)
    const [isReverting, setIsReverting] = useState(false)
    const [sort, setSort] = useState<SortConfig>({
        field: 'date',
        order: 'desc',
    })

    const handleSort = (field: SortField) => {
        setSort((prev) => ({
            field,
            order:
                prev.field === field && prev.order === 'asc' ? 'desc' : 'asc',
        }))
    }

    const sortedMergeHistory = mergeHistory
        ? [...mergeHistory].sort((a, b) => {
              const multiplier = sort.order === 'asc' ? 1 : -1

              switch (sort.field) {
                  case 'user':
                      return (
                          multiplier *
                          a.created_by.name.localeCompare(b.created_by.name)
                      )
                  case 'date':
                      return (
                          multiplier *
                          (new Date(a.created_date).getTime() -
                              new Date(b.created_date).getTime())
                      )
                  case 'operations':
                      return (
                          multiplier *
                          ((a.operations?.length || 0) -
                              (b.operations?.length || 0))
                      )
                  default:
                      return 0
              }
          })
        : null

    const handleViewOperations = (transaction: MergeTransaction) => {
        setSelectedTransaction(transaction)
        setIsRevertDialogOpen(true)
    }

    const handleRevertTransaction = async () => {
        if (!selectedTransaction) return
        setIsReverting(true)

        try {
            await revertMergeTransaction({
                transactionId: selectedTransaction.id,
            })
            setIsRevertDialogOpen(false)
        } catch (error) {
            console.error('Error reverting:', error)
        } finally {
            setIsReverting(false)
        }
    }

    if (isLoading) {
        return (
            <Container
                style={{
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                }}
            >
                <CircularProgress />
            </Container>
        )
    }

    return (
        <Container
            style={{
                flexDirection: 'column',
                padding: theme.spacing(2),
                height: '100%',
            }}
        >
            <Typography
                variant="h5"
                gutterBottom
                style={{ marginBottom: theme.spacing(3) }}
            >
                Merge History
            </Typography>

            <TableContainer
                component={Paper}
                style={{
                    maxHeight: 'calc(100vh - 200px)',
                    height: '100%',
                    overflow: 'auto',
                }}
            >
                <Table stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell
                                style={{
                                    fontWeight: 600,
                                    backgroundColor:
                                        theme.palette.background.paper,
                                }}
                            >
                                <TableSortLabel
                                    active={sort.field === 'user'}
                                    direction={
                                        sort.field === 'user'
                                            ? sort.order
                                            : 'asc'
                                    }
                                    onClick={() => handleSort('user')}
                                >
                                    User
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                style={{
                                    fontWeight: 600,
                                    backgroundColor:
                                        theme.palette.background.paper,
                                }}
                            >
                                <TableSortLabel
                                    active={sort.field === 'date'}
                                    direction={
                                        sort.field === 'date'
                                            ? sort.order
                                            : 'asc'
                                    }
                                    onClick={() => handleSort('date')}
                                >
                                    Date
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                style={{
                                    fontWeight: 600,
                                    backgroundColor:
                                        theme.palette.background.paper,
                                }}
                            >
                                <TableSortLabel
                                    active={sort.field === 'operations'}
                                    direction={
                                        sort.field === 'operations'
                                            ? sort.order
                                            : 'asc'
                                    }
                                    onClick={() => handleSort('operations')}
                                >
                                    Operations
                                </TableSortLabel>
                            </TableCell>
                            <TableCell
                                style={{
                                    fontWeight: 600,
                                    backgroundColor:
                                        theme.palette.background.paper,
                                }}
                            >
                                Status
                            </TableCell>
                            <TableCell
                                style={{
                                    fontWeight: 600,
                                    backgroundColor:
                                        theme.palette.background.paper,
                                    width: 100,
                                }}
                            >
                                Actions
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {sortedMergeHistory?.map((transaction) => (
                            <TableRow
                                key={transaction.id}
                                hover
                                style={{ cursor: 'pointer' }}
                                onClick={() =>
                                    handleViewOperations(transaction)
                                }
                            >
                                <TableCell>
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                        }}
                                    >
                                        <span>
                                            {transaction.created_by.name}
                                        </span>
                                        <span
                                            style={{
                                                fontSize: '0.8rem',
                                                color:
                                                    theme.palette.text
                                                        .secondary,
                                            }}
                                        >
                                            {transaction.created_by.email}
                                        </span>
                                    </div>
                                </TableCell>
                                <TableCell>
                                    {format(
                                        new Date(transaction.created_date),
                                        'MMM d, yyyy h:mm a',
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Typography
                                        variant="body2"
                                        style={{
                                            color: theme.palette.primary.main,
                                            fontWeight: 500,
                                        }}
                                    >
                                        {transaction.operations?.length || 0}{' '}
                                        merges
                                    </Typography>
                                </TableCell>
                                <TableCell>
                                    <Chip
                                        label={getStatusLabel(
                                            transaction.status,
                                        )}
                                        size="small"
                                        style={{
                                            backgroundColor: getStatusColor(
                                                transaction.status,
                                                theme,
                                            ),
                                            color: theme.palette.common.white,
                                            fontWeight: 500,
                                        }}
                                    />
                                </TableCell>
                                <TableCell>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            handleViewOperations(transaction)
                                        }}
                                        title="View Operations"
                                    >
                                        <MoreVertIcon />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        )) || (
                            <TableRow>
                                <TableCell colSpan={5} align="center">
                                    <Typography
                                        variant="body1"
                                        style={{
                                            color: theme.palette.text.secondary,
                                            padding: theme.spacing(4),
                                        }}
                                    >
                                        No merge history available
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            <Dialog
                open={isRevertDialogOpen}
                onClose={() => setIsRevertDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Merge Operations</DialogTitle>
                <DialogContent>
                    <List>
                        {selectedTransaction?.operations.map((operation) => (
                            <ListItem key={operation.id}>
                                <ListItemText
                                    primary={
                                        <div
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: theme.spacing(1),
                                            }}
                                        >
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: theme.spacing(1),
                                                }}
                                            >
                                                <Typography variant="body1">
                                                    {
                                                        operation
                                                            .excel_directory
                                                            ?.customer.name
                                                    }{' '}
                                                    ↔{' '}
                                                    {
                                                        operation.pdf_directory
                                                            ?.customer.name
                                                    }
                                                </Typography>
                                                <Chip
                                                    label={getOperationStatusLabel(
                                                        operation.status,
                                                    )}
                                                    size="small"
                                                    style={{
                                                        backgroundColor: getOperationStatusColor(
                                                            operation.status,
                                                            theme,
                                                        ),
                                                        color:
                                                            theme.palette.common
                                                                .white,
                                                        fontWeight: 500,
                                                        height: 20,
                                                    }}
                                                />
                                            </div>
                                            <Typography
                                                variant="caption"
                                                color="textSecondary"
                                            >
                                                Unit:{' '}
                                                {
                                                    operation.excel_directory
                                                        ?.bldg_unit
                                                }
                                            </Typography>
                                            {operation.error_message && (
                                                <Typography
                                                    variant="caption"
                                                    color="error"
                                                    style={{
                                                        marginTop: theme.spacing(
                                                            0.5,
                                                        ),
                                                    }}
                                                >
                                                    Error:{' '}
                                                    {operation.error_message}
                                                </Typography>
                                            )}
                                        </div>
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setIsRevertDialogOpen(false)}
                        color="primary"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleRevertTransaction}
                        color="primary"
                        variant="contained"
                        disabled={
                            isReverting ||
                            selectedTransaction?.status === 'REVERTED' ||
                            selectedTransaction?.status === 'PARTIALLY_REVERTED'
                        }
                    >
                        {isReverting ? (
                            <CircularProgress size={24} />
                        ) : (
                            'Revert Transaction'
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    )
}
